import {
    Card,
    InputNumber,
    Typography,
    Space,
    Row,
    Col,
    Button,
    message,
} from "antd";
import { useEffect, useState } from "react";
import { EmailListSettingCard } from "./EmailListComponent";
import axiosInstance from "../../services/axiosInstance";
import { GET_SETTINGS } from "../../services/apiEndPoints";

const { Text, Title } = Typography;

interface NumberSettingCardProps {
    title: string;
    description?: string;
    value: number;
    min?: number;
    max?: number;
    step?: number;
    onChange: (value: number) => void;
    onSave: () => void;
    isDirty: boolean;
}

const NumberSettingCard: React.FC<NumberSettingCardProps> = ({
    title,
    description,
    value,
    min = 1,
    max = 100,
    step = 1,
    onChange,
    onSave,
    isDirty,
}) => {
    return (
        <Card>
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {/* Title */}
                <Title level={5} style={{ margin: 0 }}>
                    {title}
                </Title>

                {/* Description */}
                {description && (
                    <Text type="secondary">{description}</Text>
                )}

                {/* Input */}
                <InputNumber
                    value={value}
                    min={min}
                    max={max}
                    step={step}
                    style={{ width: "100%", height: 35 }}
                    onChange={(val) => onChange(val ?? min)}
                />

                {/* Actions */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: 8,
                    }}
                >
                    <Button
                        type="primary"
                        onClick={onSave}
                        disabled={!isDirty}
                    >
                        Save
                    </Button>
                </div>
            </Space>
        </Card>
    );
};


const SettingsPage: React.FC = () => {
    const [days, setDays] = useState(10);
    const [savedDays, setSavedDays] = useState(50);

    const [emails, setEmails] = useState<string[]>([]);
    const [savedEmails, setSavedEmails] = useState<string[]>([]);

    const saveMaxUsers = async () => {
        setSavedDays(days);
        message.success("Settings saved successfully");
        await axiosInstance.put(GET_SETTINGS, {
            notificationDays: days,
            emailIds: savedEmails,
        });
    };

    const saveEmails = async () => {
        setSavedEmails(emails);
        message.success("Settings saved successfully");
        await axiosInstance.put(GET_SETTINGS, {
            notificationDays: savedDays,
            emailIds: emails,
        });
    };

    const getSettingsData = async () => {
        // Fetch settings from API and set state
        const data = await axiosInstance.get(GET_SETTINGS);
        if (data && data.data) {
            const settings = data.data;
            setDays(settings.notificationDays || 30);
            setSavedDays(settings.notificationDays || 30);
            setEmails(settings.emailIds || []);
            setSavedEmails(settings.emailIds || []);
        }
    };

    useEffect(() => {
        getSettingsData();
    }, []);

    return (
        <Row gutter={[24, 24]}>
            <Col xs={24} md={12}>
                <NumberSettingCard
                    title="How many days before should reminders be sent?"
                    description="Set the number of days before an event to send reminder notifications to users."
                    value={days}
                    min={1}
                    max={100}
                    onChange={setDays}
                    onSave={saveMaxUsers}
                    isDirty={days !== savedDays}
                />
            </Col>

            <Col xs={24} md={12}>
                <EmailListSettingCard
                    title="Notification Recipients"
                    description="Add email addresses that should receive reminder notifications."
                    value={emails}
                    onChange={setEmails}
                    onSave={saveEmails}
                    isDirty={
                        JSON.stringify(emails) !==
                        JSON.stringify(savedEmails)
                    }
                />
            </Col>
        </Row>
    );
};


export default SettingsPage;
